import { useMemo, useState } from 'react';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { filterServiceRequests } from 'src/pages/ServiceRequests/helpers/filterServiceRequests';
import { usePersistedToggle } from 'src/SharedCustomHooks/UsePersistedToggle/usePersistedToggle';
import { TOGGLE_KEYS } from 'src/store/slices/persistedUiSlice';

export interface UseServiceRequestsPageFormStateArgs {
  serviceRequests: IServiceRequest[];
}

export interface UseServiceRequestsPageFormStateReturn {
  searchString: string;
  setSearchString: (value: string) => void;

  selectedIds: string[];
  selectedServiceRequests: IServiceRequest[];
  setSelectedIds: (ids: string[]) => void;
  clearSelection: () => void;

  allSelected: boolean;
  setAllSelected: (value: boolean) => void;
  selectedCount: number;
  idsForExport: string[];

  showResolved: boolean;
  setShowResolved: (value: boolean) => void;

  showSubscribedOnly: boolean;
  setShowSubscribedOnly: (value: boolean) => void;

  visibleServiceRequests: IServiceRequest[];

  showColumnSelectionDialog: boolean;
  openColumnSelectionDialog: () => void;
  closeColumnSelectionDialog: () => void;

  showStatusManager: boolean;
  openStatusManager: () => void;
  closeStatusManager: () => void;

  showFilterOverlay: boolean;
  openFilterOverlay: () => void;
  closeFilterOverlay: () => void;

  showGroupingOverlay: boolean;
  openGroupingOverlay: () => void;
  closeGroupingOverlay: () => void;
  groupingPropertyId: string | null;
  setGroupingPropertyId: (value: string | null) => void;

  showDeclinedStatusOverlay: boolean;
  openDeclinedStatusOverlay: () => void;
  closeDeclinedStatusOverlay: () => void;

  showAssignedStatusOverlay: boolean;
  openAssignedStatusOverlay: () => void;
  closeAssignedStatusOverlay: () => void;

  showDownloadOverlay: boolean;
  openDownloadOverlay: () => void;
  closeDownloadOverlay: () => void;

  showVisibilityDialogue: boolean;
  openVisibilityDialogue: () => void;
  closeVisibilityDialogue: () => void;

  reset: () => void;
}

export const useServiceRequestsPageFormState = ({
  serviceRequests,
}: UseServiceRequestsPageFormStateArgs): UseServiceRequestsPageFormStateReturn => {
  const [searchString, setSearchString] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [allSelected, setAllSelected] = useState<boolean>(false);

  const [showColumnSelectionDialog, setShowColumnSelectionDialog] =
    useState<boolean>(false);
  const [showStatusManager, setShowStatusManager] = useState<boolean>(false);
  const [showFilterOverlay, setShowFilterOverlay] = useState<boolean>(false);
  const [showGroupingOverlay, setShowGroupingOverlay] =
    useState<boolean>(false);
  const [groupingPropertyId, setGroupingPropertyId] = useState<string | null>(
    null
  );

  const [showDeclinedStatusOverlay, setShowDeclinedStatusOverlay] =
    useState<boolean>(false);
  const [showAssignedStatusOverlay, setShowAssignedStatusOverlay] =
    useState<boolean>(false);
  const [showDownloadOverlay, setShowDownloadOverlay] =
    useState<boolean>(false);
  const [showVisibilityDialogue, setShowVisibilityDialogue] =
    useState<boolean>(false);

  const [showResolved, setShowResolved] = usePersistedToggle(
    TOGGLE_KEYS.serviceRequestDashboardShowResolved
  );
  const [showSubscribedOnly, setShowSubscribedOnly] = usePersistedToggle(
    TOGGLE_KEYS.serviceRequestDashboardSubscribedOnly
  );

  const visibleServiceRequests: IServiceRequest[] = useMemo(() => {
    return filterServiceRequests(serviceRequests, {
      searchString,
      subscribedOnly: showSubscribedOnly,
    });
  }, [serviceRequests, searchString, showSubscribedOnly]);

  const selectedServiceRequests: IServiceRequest[] = useMemo(() => {
    const idSet: Set<string> = new Set(selectedIds);

    return (
      serviceRequests?.filter((sr: IServiceRequest) => idSet.has(sr.id)) ?? []
    );
  }, [serviceRequests, selectedIds]);

  const selectedCount: number = allSelected
    ? serviceRequests.length
    : selectedIds.length;

  const idsForExport: string[] = useMemo(() => {
    if (allSelected) {
      return serviceRequests.map((sr: IServiceRequest) => sr.id);
    }

    return selectedIds;
  }, [allSelected, serviceRequests, selectedIds]);

  const clearSelection = (): void => {
    setSelectedIds([]);
  };

  const reset = (): void => {
    setSearchString('');
    setSelectedIds([]);
    setAllSelected(false);
    setShowResolved(false);
    setShowSubscribedOnly(false);
    setShowColumnSelectionDialog(false);
    setShowStatusManager(false);
    setShowFilterOverlay(false);
    setShowGroupingOverlay(false);
    setGroupingPropertyId(null);
    setShowDeclinedStatusOverlay(false);
    setShowAssignedStatusOverlay(false);
    setShowDownloadOverlay(false);
    setShowVisibilityDialogue(false);
  };

  return {
    searchString,
    setSearchString,

    selectedIds,
    selectedServiceRequests,
    setSelectedIds,
    clearSelection,

    allSelected,
    setAllSelected,
    selectedCount,
    idsForExport,

    showResolved,
    setShowResolved,

    showSubscribedOnly,
    setShowSubscribedOnly,

    visibleServiceRequests,

    showColumnSelectionDialog,
    openColumnSelectionDialog: () => setShowColumnSelectionDialog(true),
    closeColumnSelectionDialog: () => setShowColumnSelectionDialog(false),

    showStatusManager,
    openStatusManager: () => setShowStatusManager(true),
    closeStatusManager: () => setShowStatusManager(false),

    showFilterOverlay,
    openFilterOverlay: () => setShowFilterOverlay(true),
    closeFilterOverlay: () => setShowFilterOverlay(false),

    showGroupingOverlay,
    openGroupingOverlay: () => setShowGroupingOverlay(true),
    closeGroupingOverlay: () => setShowGroupingOverlay(false),
    groupingPropertyId,
    setGroupingPropertyId,

    showDeclinedStatusOverlay,
    openDeclinedStatusOverlay: () => setShowDeclinedStatusOverlay(true),
    closeDeclinedStatusOverlay: () => setShowDeclinedStatusOverlay(false),

    showAssignedStatusOverlay,
    openAssignedStatusOverlay: () => setShowAssignedStatusOverlay(true),
    closeAssignedStatusOverlay: () => setShowAssignedStatusOverlay(false),

    showDownloadOverlay,
    openDownloadOverlay: () => setShowDownloadOverlay(true),
    closeDownloadOverlay: () => setShowDownloadOverlay(false),

    showVisibilityDialogue,
    openVisibilityDialogue: () => setShowVisibilityDialogue(true),
    closeVisibilityDialogue: () => setShowVisibilityDialogue(false),

    reset,
  };
};
